import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { updateThemePreference, fetchThemePreference } from '@/lib/api';

const ThemeContext = createContext();

// Storage key for auth data
const STORAGE_KEY = "wrvu_auth";

function isAuthenticated() {
  return !!(localStorage.getItem(STORAGE_KEY) || sessionStorage.getItem(STORAGE_KEY));
}

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => {
    const saved = localStorage.getItem('appTheme');
    // Default to 'light' for new users
    return saved || 'light';
  });
  const [syncing, setSyncing] = useState(false);

  // Sync theme with backend when user is authenticated
  const syncThemeToBackend = useCallback(async (newTheme) => {
    if (!isAuthenticated()) return;
    
    try {
      setSyncing(true);
      await updateThemePreference(newTheme);
    } catch (err) {
      console.log('Failed to sync theme preference to backend:', err);
    } finally {
      setSyncing(false);
    }
  }, []);

  // Load theme from backend on mount if authenticated
  useEffect(() => {
    const loadThemeFromBackend = async () => {
      if (!isAuthenticated()) return;
      
      try {
        const data = await fetchThemePreference();
        if (data.theme && data.theme !== theme) {
          setTheme(data.theme);
          localStorage.setItem('appTheme', data.theme);
        }
      } catch (err) {
        console.log('Failed to fetch theme preference from backend:', err);
      }
    };

    loadThemeFromBackend();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    localStorage.setItem('appTheme', theme);
    // Update document class for global styling - this triggers CSS variable changes
    document.documentElement.classList.remove('dark', 'light');
    document.documentElement.classList.add(theme);
    // Also add to root for CSS variable switching
    if (theme === 'light') {
      document.documentElement.classList.add('light');
    }
  }, [theme]);

  const toggleTheme = useCallback(() => {
    setTheme(prev => {
      const newTheme = prev === 'dark' ? 'light' : 'dark';
      // Sync to backend (async, don't block UI)
      syncThemeToBackend(newTheme);
      return newTheme;
    });
  }, [syncThemeToBackend]);

  // Function to set theme directly (used when loading from user data on login)
  const setThemeDirectly = useCallback((newTheme) => {
    if (newTheme === 'dark' || newTheme === 'light') {
      setTheme(newTheme);
      localStorage.setItem('appTheme', newTheme);
    }
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, setThemeDirectly, syncing }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
}
